import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Rocket, Beaker, ArrowLeft, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import {
  usePricingTimeline,
  TimelineBundle,
  TimelineOffering,
} from "@/features/bundles/services/get-pricing-timeline";
import { useFullRolloutBundle } from "@/features/bundles/hooks/use-pricing-lifecycle";
import PriceChangeDialog from "@/features/bundles/ui/price-change-dialog";
import CohortTimelineView from "@/features/bundles/ui/cohort-timeline-view";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { cn } from "@/lib/utils";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";

const BAR_COLOR = "#A5B4FC"; // indigo-300 — cohort size
const BAR_COLOR_ACTIVE = "#6366F1"; // indigo-500 — newest (still-growing) cohort
const LINE_COLOR = "#EA5B34"; // brand orange — price

const fmtDate = (iso: string) => format(new Date(iso), "dd MMM yyyy");

// Human cohort label for a version, derived from the sorted release boundaries.
function cohortLabel(
  offering: TimelineOffering,
  version: TimelineBundle | null,
  index: number, // index in the asc experimental list; -1 for baseline
): string {
  const exp = offering.experimental;
  if (!version || version.released_at == null) {
    // Baseline
    return exp.length
      ? `Signed up before ${fmtDate(exp[0].released_at as string)}`
      : "Everyone";
  }
  const start = fmtDate(version.released_at);
  const next = exp[index + 1];
  return next
    ? `${start} – ${fmtDate(next.released_at as string)}`
    : `${start} onwards`;
}

type ChartRow = {
  label: string; // x-axis category (cohort start)
  range: string; // full human cohort range for the tooltip
  price: number;
  cohort: number;
  isNewest: boolean;
};

function OfferingCard({ offering }: { offering: TimelineOffering }) {
  const fullRollout = useFullRolloutBundle();
  const [partialFor, setPartialFor] = useState<TimelineBundle | null>(null);

  const { baseline, experimental, history, currency } = offering;
  const fmt = (n: number) => `${currency} ${n.toLocaleString()}`;

  // Newest release first for the list.
  const versionsDesc = [...experimental].reverse();

  // Chart: one point per cohort in chronological order (baseline → each
  // experimental). Bar = # users in that cohort; line = the price they pay.
  const chart: ChartRow[] = [];
  if (baseline) {
    chart.push({
      label: "Baseline",
      range: cohortLabel(offering, baseline, -1),
      price: baseline.amount,
      cohort: baseline.cohort_size ?? 0,
      isNewest: experimental.length === 0,
    });
  }
  experimental.forEach((e, i) => {
    chart.push({
      label: format(new Date(e.released_at as string), "dd MMM"),
      range: cohortLabel(offering, e, i),
      price: e.amount,
      cohort: e.cohort_size ?? 0,
      isNewest: i === experimental.length - 1,
    });
  });

  const totalUsers = chart.reduce((s, c) => s + c.cohort, 0);

  const handleFullRollout = (v: TimelineBundle) => {
    if (
      !window.confirm(
        `Roll out ${fmt(v.amount)} to EVERYONE? This becomes the production price for all users and retires the other versions.`,
      )
    )
      return;
    fullRollout.mutate(v.id, {
      onSuccess: () => toast({ title: "Price fully rolled out" }),
      onError: (e: unknown) =>
        toast({
          title: "Error",
          description:
            (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Full rollout failed",
          variant: "destructive",
        }),
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Header — full width */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">
            {offering.name}
          </p>
          <p className="text-[11px] font-mono text-gray-400 truncate">
            {offering.standard_id}
          </p>
        </div>
        <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
          Baseline: {baseline ? fmt(baseline.amount) : "—"}
        </span>
        <HasAccess permission="write">
          <Button
            size="sm"
            variant="outline"
            disabled={!baseline}
            onClick={() => setPartialFor(baseline)}
          >
            <Beaker className="h-3.5 w-3.5 mr-1" /> Partial release
          </Button>
        </HasAccess>
      </div>

      {/* Two-column body: chart | versions & actions */}
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
        {/* Left — cohort bars + price line */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-500">
              Price &amp; cohort size
            </p>
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
              <Users className="h-3 w-3" />
              {totalUsers.toLocaleString()} users
            </span>
          </div>
          {chart.length >= 1 ? (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chart}
                  margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="users"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    width={36}
                  />
                  <YAxis
                    yAxisId="price"
                    orientation="right"
                    tickFormatter={(v) => `${v}`}
                    tick={{ fontSize: 11, fill: LINE_COLOR }}
                    width={48}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as ChartRow;
                      return (
                        <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-sm">
                          <p className="font-medium text-gray-900">
                            {fmt(row.price)}
                          </p>
                          <p className="text-gray-500">{row.range}</p>
                          <p className="mt-1 inline-flex items-center gap-1 text-indigo-600">
                            <Users className="h-3 w-3" />
                            {row.cohort.toLocaleString()} users
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Bar
                    yAxisId="users"
                    dataKey="cohort"
                    name="Users"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={56}
                  >
                    {chart.map((row, i) => (
                      <Cell
                        key={i}
                        fill={row.isNewest ? BAR_COLOR_ACTIVE : BAR_COLOR}
                      />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="price"
                    type="monotone"
                    dataKey="price"
                    name={`Price (${currency})`}
                    stroke={LINE_COLOR}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: LINE_COLOR }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-xs text-gray-400">
              No active prices to chart.
            </div>
          )}
        </div>

        {/* Right — versions (newest first), baseline, history */}
        <div className="flex flex-col">
          <div className="divide-y divide-gray-100">
            {versionsDesc.map((v) => {
              const idx = experimental.findIndex((e) => e.id === v.id);
              return (
                <div key={v.id} className="flex items-center gap-3 px-5 py-3">
                  <Badge
                    variant="outline"
                    className="text-[10px] border-amber-500 text-amber-700 shrink-0"
                  >
                    Experiment
                  </Badge>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{fmt(v.amount)}</p>
                    <p className="text-[11px] text-gray-500">
                      {cohortLabel(offering, v, idx)}
                    </p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-400 shrink-0">
                    <Users className="h-3 w-3" />
                    {(v.cohort_size ?? 0).toLocaleString()}
                  </span>
                  <HasAccess permission="write">
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      disabled={fullRollout.isPending}
                      onClick={() => handleFullRollout(v)}
                    >
                      <Rocket className="h-3.5 w-3.5 mr-1" /> Full rollout
                    </Button>
                  </HasAccess>
                </div>
              );
            })}
            {baseline && (
              <div className="flex items-center gap-3 px-5 py-3 bg-gray-50/50">
                <Badge variant="outline" className="text-[10px] shrink-0">
                  Baseline
                </Badge>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{fmt(baseline.amount)}</p>
                  <p className="text-[11px] text-gray-500">
                    {cohortLabel(offering, baseline, -1)}
                  </p>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-400 shrink-0">
                  <Users className="h-3 w-3" />
                  {(baseline.cohort_size ?? 0).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="mt-auto px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-400">
              {history.length} retired price{history.length === 1 ? "" : "s"}:{" "}
              {history.map((h) => fmt(h.amount)).join(", ")}
            </div>
          )}
        </div>
      </div>

      {/* Partial release reuses the merged Change Price dialog (partial mode). */}
      <PriceChangeDialog
        bundle={
          partialFor ? (partialFor as unknown as GroupedBundleLeaf) : null
        }
        history={[]}
        initialMode="SET"
        latestReleaseAt={
          experimental[experimental.length - 1]?.released_at ?? null
        }
        onClose={() => setPartialFor(null)}
      />
    </div>
  );
}

export default function PricingTimeline() {
  const { region } = useParams<{ region: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const standardId = params.get("standard_id") ?? undefined;
  const productId = params.get("product_id") ?? undefined;

  const { data, isLoading, isError } = usePricingTimeline({
    standard_id: standardId,
    product_id: productId,
    region,
  });

  const offerings = data?.offerings ?? [];

  // View preference resolves URL (?view=) → localStorage → default, and writes
  // to both on change (mirrors the bundles-page filter precedence).
  const [storedView, setStoredView] = useLocalStorage<"offering" | "cohort">(
    "pricing.timeline.view",
    "offering",
  );
  const urlView = params.get("view");
  const view: "offering" | "cohort" =
    urlView === "offering" || urlView === "cohort" ? urlView : storedView;
  const setView = (next: "offering" | "cohort") => {
    setStoredView(next);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("view", next);
        return p;
      },
      { replace: true },
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Pricing timeline</h1>
          <p className="text-xs text-muted-foreground">
            {standardId
              ? `Offering ${standardId}`
              : productId
                ? `Product ${productId}`
                : "Pass ?standard_id= or ?product_id="}
          </p>
        </div>

        {/* View toggle: per-offering (default) vs cohort-wise across offerings. */}
        {offerings.length > 0 && (
          <div className="ml-auto inline-flex rounded-md border border-gray-200 p-0.5">
            {(
              [
                { key: "offering", label: "By offering" },
                { key: "cohort", label: "By cohort" },
              ] as const
            ).map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => setView(v.key)}
                className={cn(
                  "h-8 rounded px-3 text-xs font-medium transition-colors",
                  view === v.key
                    ? "bg-[#EA5B34] text-white"
                    : "text-gray-500 hover:bg-gray-50",
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!standardId && !productId ? (
        <p className="text-sm text-amber-600">
          Add a <code>?standard_id=</code> or <code>?product_id=</code> query
          param to view a pricing timeline.
        </p>
      ) : isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-500">Couldn&apos;t load pricing timeline.</p>
      ) : offerings.length === 0 ? (
        <p className="text-sm text-gray-400">
          No offerings found for this {standardId ? "standard_id" : "product"} in{" "}
          {region}.
        </p>
      ) : view === "cohort" ? (
        <CohortTimelineView offerings={offerings} />
      ) : (
        <div className="space-y-4">
          {offerings.map((o) => (
            <OfferingCard key={o.standard_id} offering={o} />
          ))}
        </div>
      )}
    </div>
  );
}
